import type { CostReport } from "@/lib/api/types";
import { BUDGET_STATE_CLASS, BUDGET_STATE_TEXT, barWidth, monthLabel, percent } from "@/lib/cost-report";
import { formatEuro } from "@/lib/format";

/** Euro-Kosten gegen das Monatsbudget, mit Limit je Job und Hochrechnung. */
export function BudgetCard({ report }: { report: CostReport }) {
  const { budget } = report;
  const hasBudget = budget.state !== "none";
  return (
    <section aria-labelledby="budget-title" className="flex flex-col gap-3 rounded-lg border border-line bg-white px-5 py-[18px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="budget-title" className="text-base font-semibold">
          Monatsbudget {monthLabel(report.month)}
        </h2>
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${BUDGET_STATE_CLASS[budget.state]}`}>
          {BUDGET_STATE_TEXT[budget.state]}
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span className="text-[34px] font-semibold leading-none tracking-tight">{formatEuro(budget.spent_eur)}</span>
        {hasBudget && (
          <span className="text-sm text-muted">
            von {formatEuro(budget.monthly_eur)} ({percent(budget.share)} %)
          </span>
        )}
      </div>

      {hasBudget && (
        <div
          role="meter"
          aria-label="Verbrauchtes Monatsbudget"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(100, percent(budget.share))}
          aria-valuetext={`${percent(budget.share)} % von ${formatEuro(budget.monthly_eur)}`}
          className="h-2.5 overflow-hidden rounded bg-[#e9ecea]"
        >
          <div
            className={`h-full ${budget.state === "over" ? "bg-danger" : budget.state === "warn" ? "bg-warning" : "bg-accent"}`}
            style={{ width: `${barWidth(budget.share)}%` }}
          />
        </div>
      )}

      <ul className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-muted">
        <li>Limit pro Job: {formatEuro(budget.job_limit_eur)}</li>
        {budget.forecast_eur !== null && <li>Hochrechnung bis Monatsende: {formatEuro(budget.forecast_eur)}</li>}
      </ul>
      <p className="text-xs text-muted">
        Euro-Kosten fallen bei DataForSEO an. ahrefs (Units), Firecrawl (Credits) und Gemini (Tokens) laufen über das Abo
        oder Guthaben der Quelle und stehen in der Tabelle unten mit ihrem Verbrauch. Ein Job mit Euro-Kosten, der den Rest
        des Monatsbudgets überschreitet, wartet auf Freigabe.
      </p>
    </section>
  );
}
